import Phaser from 'phaser';

/**
 * MainScene - Phaser scene for particle effects and visual polish
 * Used for space phase star field, celebrations, and visual feedback
 */
export class MainScene extends Phaser.Scene {
  private starField?: Phaser.GameObjects.Particles.ParticleEmitter;
  private isSpacePhase = false;
  private colorShiftActive = false;
  private realityUnravelingActive = false;

  constructor() {
    super({ key: 'MainScene' });
  }

  create() {
    // Create particle textures
    this.createParticleTextures();

    // Start with star field hidden (enabled during space phase)
    this.setupStarField();
  }

  createParticleTextures() {
    // Create star texture (small circle)
    const starGraphics = this.make.graphics({ x: 0, y: 0 });
    starGraphics.fillStyle(0xffffff, 1);
    starGraphics.fillCircle(4, 4, 2);
    starGraphics.generateTexture('star', 8, 8);
    starGraphics.destroy();

    // Create glow texture (soft circle for effects)
    const glowGraphics = this.make.graphics({ x: 0, y: 0 });
    // Create a soft glow effect with layered circles
    glowGraphics.fillStyle(0x00d4ff, 0.2);
    glowGraphics.fillCircle(16, 16, 16);
    glowGraphics.fillStyle(0x00d4ff, 0.4);
    glowGraphics.fillCircle(16, 16, 12);
    glowGraphics.fillStyle(0x00d4ff, 0.6);
    glowGraphics.fillCircle(16, 16, 8);
    glowGraphics.fillStyle(0x00d4ff, 0.8);
    glowGraphics.fillCircle(16, 16, 4);
    glowGraphics.generateTexture('glow', 32, 32);
    glowGraphics.destroy();

    // Create spark texture
    const sparkGraphics = this.make.graphics({ x: 0, y: 0 });
    sparkGraphics.fillStyle(0x00ff88, 1);
    sparkGraphics.fillRect(0, 2, 8, 2);
    sparkGraphics.fillRect(2, 0, 2, 6);
    sparkGraphics.generateTexture('spark', 8, 6);
    sparkGraphics.destroy();

    // PSYCHEDELIC ADDITIONS: Create rainbow gradient texture
    const rainbowGraphics = this.make.graphics({ x: 0, y: 0 });
    const rainbowColors = [0xff0000, 0xff7700, 0xffff00, 0x00ff00, 0x0088ff, 0x8800ff, 0xff00ff];
    for (let i = 0; i < rainbowColors.length; i++) {
      const radius = 16 - (i * 2);
      rainbowGraphics.fillStyle(rainbowColors[i]!, 0.6 - (i * 0.08));
      rainbowGraphics.fillCircle(16, 16, radius);
    }
    rainbowGraphics.generateTexture('rainbow', 32, 32);
    rainbowGraphics.destroy();

    // Create kaleidoscope segment texture
    const kaleidoGraphics = this.make.graphics({ x: 0, y: 0 });
    kaleidoGraphics.fillStyle(0xff00ff, 1);
    kaleidoGraphics.fillTriangle(8, 0, 16, 8, 8, 16);
    kaleidoGraphics.fillStyle(0x00ffff, 0.7);
    kaleidoGraphics.fillTriangle(0, 8, 8, 0, 8, 16);
    kaleidoGraphics.generateTexture('kaleido', 16, 16);
    kaleidoGraphics.destroy();

    // Create neon ring texture
    const neonGraphics = this.make.graphics({ x: 0, y: 0 });
    neonGraphics.lineStyle(3, 0xff00ff, 1);
    neonGraphics.strokeCircle(16, 16, 12);
    neonGraphics.lineStyle(2, 0x00ffff, 1);
    neonGraphics.strokeCircle(16, 16, 8);
    neonGraphics.generateTexture('neon', 32, 32);
    neonGraphics.destroy();
  }

  setupStarField() {
    // PSYCHEDELIC: Create multi-colored starfield
    const starColors = [0xffffff, 0x00ffff, 0xff00ff, 0xffff00, 0x00ff88, 0xff6600];
    
    this.starField = this.add.particles(0, 0, 'star', {
      x: { min: 0, max: this.scale.width },
      y: -10,
      lifespan: 6000,
      speedY: { min: 50, max: 150 },
      speedX: { min: -10, max: 10 },
      scale: { start: 0.5, end: 0, random: true },
      alpha: { start: 0.8, end: 0 },
      quantity: 2,
      frequency: 100,
      tint: starColors,
      blendMode: Phaser.BlendModes.ADD,
    });

    // Start paused (will enable during space phase)
    this.starField.stop();
  }

  /**
   * Enable space phase visual effects
   */
  enableSpacePhase() {
    if (this.isSpacePhase) return;
    this.isSpacePhase = true;

    // Start star field
    if (this.starField) {
      this.starField.start();
    }
  }

  /**
   * Disable space phase visual effects
   */
  disableSpacePhase() {
    if (!this.isSpacePhase) return;
    this.isSpacePhase = false;

    if (this.starField) {
      this.starField.stop();
    }
  }

  /**
   * Show clip production burst effect - PSYCHEDELIC ENHANCED
   */
  showClipBurst(x: number, y: number, amount: number) {
    const intensity = Math.min(Math.log10(amount + 1) * 10, 50);
    const particleCount = Math.min(Math.floor(intensity / 5) + 1, 10);

    // PSYCHEDELIC: Color based on production rate
    const colors = [0x00ff88, 0x00ffff, 0xff00ff, 0xffff00, 0xff6600];
    const colorIndex = Math.floor((amount / 1000) % colors.length);
    const burstColor = colors[colorIndex] || 0x00ff88;

    // Main spark burst
    this.add.particles(x, y, 'spark', {
      speed: { min: 50, max: 100 + intensity },
      angle: { min: 0, max: 360 },
      lifespan: 400,
      quantity: particleCount,
      scale: { start: 0.5, end: 0 },
      alpha: { start: 1, end: 0 },
      tint: burstColor,
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    }).explode(particleCount);

    // PSYCHEDELIC: Rainbow trail for large bursts
    if (amount >= 500) {
      this.add.particles(x, y, 'rainbow', {
        speed: { min: 30, max: 80 },
        angle: { min: 0, max: 360 },
        lifespan: 600,
        quantity: Math.floor(particleCount / 2),
        scale: { start: 0.4, end: 0 },
        alpha: { start: 0.7, end: 0 },
        blendMode: Phaser.BlendModes.ADD,
        emitting: false,
      }).explode(Math.floor(particleCount / 2));
    }

    // PSYCHEDELIC: Subtle screen shake for massive bursts
    if (amount >= 10000) {
      this.cameras.main.shake(100, 0.003);
    }
  }

  /**
   * Show milestone celebration effect - PSYCHEDELIC ENHANCED
   */
  showMilestoneEffect() {
    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;

    // PSYCHEDELIC: Color palette cycling
    const psychedelicColors = [
      0xff00ff, // Magenta
      0x00ffff, // Cyan
      0xffff00, // Yellow
      0xff0088, // Hot pink
      0x00ff88, // Mint green
      0x8800ff, // Purple
      0xff6600, // Orange
    ];

    // PSYCHEDELIC: Kaleidoscope burst pattern (6-fold symmetry)
    const segments = 6;
    const particlesPerSegment = 5;
    
    for (let seg = 0; seg < segments; seg++) {
      const segmentAngle = (seg / segments) * Math.PI * 2;
      
      for (let i = 0; i < particlesPerSegment; i++) {
        const angle = segmentAngle + (i / particlesPerSegment) * (Math.PI / segments);
        const colorIndex = (seg + i) % psychedelicColors.length;

        // Main kaleidoscope burst
        this.add.particles(centerX, centerY, 'kaleido', {
          speed: { min: 150, max: 250 },
          angle: Phaser.Math.RadToDeg(angle),
          lifespan: 1200,
          quantity: 1,
          scale: { start: 1, end: 0 },
          alpha: { start: 1, end: 0 },
          tint: psychedelicColors[colorIndex],
          blendMode: Phaser.BlendModes.ADD,
          rotate: { start: 0, end: 360 },
          emitting: false,
        }).explode(1);

        // Rainbow glow trail
        this.add.particles(centerX, centerY, 'rainbow', {
          speed: { min: 100, max: 200 },
          angle: Phaser.Math.RadToDeg(angle),
          lifespan: 1500,
          quantity: 1,
          scale: { start: 0.8, end: 0 },
          alpha: { start: 0.8, end: 0 },
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        }).explode(1);
      }
    }

    // PSYCHEDELIC: Screen shake
    this.cameras.main.shake(200, 0.008);

    // PSYCHEDELIC: Neon ring expansion
    for (let ring = 0; ring < 3; ring++) {
      this.time.delayedCall(ring * 100, () => {
        this.add.particles(centerX, centerY, 'neon', {
          speed: 0,
          lifespan: 800,
          quantity: 1,
          scale: { start: 0.5 + ring * 0.3, end: 2 + ring },
          alpha: { start: 1, end: 0 },
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        }).explode(1);
      });
    }
  }

  /**
   * Show probe launch effect - PSYCHEDELIC ENHANCED
   */
  showProbeLaunch(x: number, y: number) {
    // PSYCHEDELIC: Rainbow launch trail
    this.add.particles(x, y, 'rainbow', {
      speed: { min: 100, max: 200 },
      angle: { min: 180, max: 360 },
      lifespan: 800,
      quantity: 20,
      scale: { start: 0.5, end: 0 },
      alpha: { start: 1, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    }).explode(20);

    // PSYCHEDELIC: Neon ring expansion
    this.add.particles(x, y, 'neon', {
      speed: 0,
      lifespan: 600,
      quantity: 3,
      scale: { start: 0.3, end: 1.5 },
      alpha: { start: 1, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    }).explode(3);

    // PSYCHEDELIC: Cyan glow burst
    this.add.particles(x, y, 'glow', {
      speed: { min: 50, max: 100 },
      angle: { min: 0, max: 360 },
      lifespan: 600,
      quantity: 15,
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.8, end: 0 },
      tint: 0x00ffff,
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    }).explode(15);
  }

  /**
   * Show combat effect - PSYCHEDELIC ENHANCED
   */
  showCombatEffect(x: number, y: number) {
    // PSYCHEDELIC: Multi-color explosion
    const explosionColors = [0xff4060, 0xff00ff, 0xffff00, 0xff6600];
    
    for (let i = 0; i < explosionColors.length; i++) {
      const angle = (i / explosionColors.length) * 360;
      this.add.particles(x, y, 'spark', {
        speed: { min: 150, max: 300 },
        angle: { min: angle - 20, max: angle + 20 },
        lifespan: 500,
        quantity: 8,
        scale: { start: 0.7, end: 0 },
        alpha: { start: 1, end: 0 },
        tint: explosionColors[i],
        blendMode: Phaser.BlendModes.ADD,
        rotate: { start: 0, end: 360 },
        emitting: false,
      }).explode(8);
    }

    // PSYCHEDELIC: Kaleidoscope shockwave
    for (let ring = 0; ring < 3; ring++) {
      this.time.delayedCall(ring * 50, () => {
        this.add.particles(x, y, 'kaleido', {
          speed: 200 + ring * 50,
          angle: { min: 0, max: 360 },
          lifespan: 400,
          quantity: 12,
          scale: { start: 0.8, end: 0 },
          alpha: { start: 0.9, end: 0 },
          tint: explosionColors[ring % explosionColors.length],
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        }).explode(12);
      });
    }

    // PSYCHEDELIC: Screen shake
    this.cameras.main.shake(150, 0.01);
  }

  /**
   * Show glitch effect - PSYCHEDELIC ENHANCEMENT
   * Creates a reality-breaking glitch effect
   */
  showGlitchEffect(intensity: number = 1) {
    const duration = 200 * intensity;
    const glitchCount = Math.floor(3 * intensity);

    for (let i = 0; i < glitchCount; i++) {
      this.time.delayedCall(i * 50, () => {
        // Create glitch graphics
        const glitch = this.make.graphics({ x: 0, y: 0 });
        const glitchX = Phaser.Math.Between(0, this.scale.width);
        const glitchY = Phaser.Math.Between(0, this.scale.height);
        const glitchWidth = Phaser.Math.Between(50, 200);
        const glitchHeight = Phaser.Math.Between(20, 100);

        // Random glitch colors
        const glitchColors = [0xff00ff, 0x00ffff, 0xffff00, 0xff0088];
        const color = glitchColors[Math.floor(Math.random() * glitchColors.length)] || 0xff00ff;

        glitch.fillStyle(color, 0.3);
        glitch.fillRect(glitchX, glitchY, glitchWidth, glitchHeight);

        // Animate glitch
        this.tweens.add({
          targets: glitch,
          alpha: 0,
          duration: duration,
          onComplete: () => {
            glitch.destroy();
          },
        });
      });
    }

    // Screen distortion
    this.cameras.main.shake(duration, 0.01 * intensity);
  }

  /**
   * Show color shift effect - PSYCHEDELIC ENHANCEMENT
   * Cycles through psychedelic colors
   * FIXED: Uses Phaser time system instead of setInterval to prevent memory leaks
   */
  showColorShiftEffect(duration: number = 1000) {
    if (this.colorShiftActive) return;
    this.colorShiftActive = true;

    const colors = [0xff00ff, 0x00ffff, 0xffff00, 0xff0088, 0x00ff88, 0x8800ff];

    let colorIndex = 0;
    
    // Use Phaser's time system instead of setInterval
    this.time.addEvent({
      delay: duration / colors.length,
      callback: () => {
        const color = colors[colorIndex % colors.length] || 0xff00ff;
        
        // Apply color shift to camera
        this.cameras.main.setBackgroundColor(color);
        colorIndex++;
      },
      repeat: colors.length - 1,
      callbackScope: this
    });

    // Reset after duration
    this.time.delayedCall(duration, () => {
      this.cameras.main.setBackgroundColor(0x0a0a0f);
      this.colorShiftActive = false;
      // Timer is automatically cleaned up after repeats complete
    });
  }

  /**
   * Show reality unraveling effect - PSYCHEDELIC ENHANCEMENT
   * Creates a surreal, reality-breaking effect for endgame
   */
  showRealityUnravelingEffect() {
    if (this.realityUnravelingActive) return;
    this.realityUnravelingActive = true;

    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;

    // Create expanding spiral of particles
    const spiralLayers = 8;
    const particlesPerLayer = 12;

    for (let layer = 0; layer < spiralLayers; layer++) {
      this.time.delayedCall(layer * 100, () => {
        for (let i = 0; i < particlesPerLayer; i++) {
          const angle = (i / particlesPerLayer) * Math.PI * 2;
          const radius = 50 + layer * 40;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;

          const psychedelicColors = [
            0xff00ff, 0x00ffff, 0xffff00, 0xff0088, 0x00ff88, 0x8800ff, 0xff6600,
          ];
          const color = psychedelicColors[Math.floor(Math.random() * psychedelicColors.length)] || 0xff00ff;

          // Create particle burst
          this.add.particles(x, y, 'rainbow', {
            speed: { min: 100, max: 200 },
            angle: Phaser.Math.RadToDeg(angle),
            lifespan: 1500,
            quantity: 3,
            scale: { start: 0.8, end: 0 },
            alpha: { start: 1, end: 0 },
            tint: color,
            blendMode: Phaser.BlendModes.ADD,
            emitting: false,
          }).explode(3);
        }
      });
    }

    // Intense screen shake
    this.cameras.main.shake(2000, 0.02);

    // Color cycling during unraveling (FIXED: using Phaser time system)
    const unravelColors = [0xff00ff, 0x00ffff, 0xffff00, 0xff0088, 0x00ff88];
    let colorIdx = 0;
    
    this.time.addEvent({
      delay: 100,
      callback: () => {
        const color = unravelColors[colorIdx % unravelColors.length] || 0xff00ff;
        this.cameras.main.setBackgroundColor(color);
        colorIdx++;
      },
      repeat: 29, // 30 color changes over 3 seconds
      callbackScope: this
    });

    // Stop after 3 seconds
    this.time.delayedCall(3000, () => {
      this.cameras.main.setBackgroundColor(0x0a0a0f);
      this.realityUnravelingActive = false;
      // Timer is automatically cleaned up
    });
  }

  /**
   * Show surreal animation - PSYCHEDELIC ENHANCEMENT
   * Creates a warping, reality-bending animation
   */
  showSurrealAnimation(x: number, y: number) {
    // Create multiple rotating kaleidoscope patterns
    const patterns = 3;
    for (let p = 0; p < patterns; p++) {
      const rotation = p * 120;
      const scale = 0.5 + p * 0.3;

      this.add.particles(x, y, 'kaleido', {
        speed: 0,
        lifespan: 1200,
        quantity: 1,
        scale: { start: scale, end: scale * 2 },
        alpha: { start: 0.8, end: 0 },
        blendMode: Phaser.BlendModes.ADD,
        rotate: { start: rotation, end: rotation + 360 },
        emitting: false,
      }).explode(1);
    }

    // Add neon rings
    for (let ring = 0; ring < 3; ring++) {
      this.time.delayedCall(ring * 150, () => {
        this.add.particles(x, y, 'neon', {
          speed: 0,
          lifespan: 800,
          quantity: 1,
          scale: { start: 0.3 + ring * 0.2, end: 1.5 + ring * 0.3 },
          alpha: { start: 1, end: 0 },
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        }).explode(1);
      });
    }
  }

  /**
   * Trigger psychedelic milestone - PSYCHEDELIC ENHANCEMENT
   * Enhanced version of milestone effect with more intensity
   */
  showPsychedelicMilestone() {
    // Show all effects together
    this.showMilestoneEffect();
    this.showColorShiftEffect(500);
    this.showGlitchEffect(0.5);
  }

  update(_time: number, _delta: number) {
    // Update particle bounds on resize
    if (this.starField) {
      this.starField.setPosition(0, 0);
    }
  }
}
